import { useCallback, useMemo, useRef, useState } from 'react';
import { useToast } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';

import { createMembers, validateApiResponse } from '../../../services';
import { USER_TYPE } from '../constants/copy.js';

/** "Maria Lopez Diaz" -> { first_name: 'Maria', last_name: 'Lopez Diaz' }; blank -> nulls. */
const splitName = (value = '') => {
  const [first, ...rest] = value.trim().split(/\s+/).filter(Boolean);
  return { first_name: first ?? null, last_name: rest.length ? rest.join(' ') : null };
};

const buildPayload = ({ emails, names, userType, roleIds, groupIds, sendEmail, note }) => {
  const isMember = userType === USER_TYPE.MEMBER;
  return {
    members: emails.map((email) => ({ email, user_type: userType, ...splitName(names[email]) })),
    send_invitation: sendEmail,
    invitation_note: sendEmail && note.trim() ? note.trim() : null,
    role_ids: isMember ? roleIds : [],
    group_ids: isMember ? groupIds : [],
  };
};

/**
 * Submits the invite builder. `onSuccess({ createdCount, sent })` runs after the success toast;
 * `submit(state, { roleNames })` takes the Member's role names for the toast. On failure `error` is set and nothing else changes, so the wizard keeps its entries.
 *
 * @param {{ onSuccess?: (result: { createdCount: number, sent: boolean }) => void }} options
 */
export const useInviteMembers = ({ onSuccess } = {}) => {
  const { t } = useTranslation('people-access');
  const toast = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // Set synchronously, before the state update re-renders the disabled button: a double click is one submit (CT-5832).
  // It stays set after a success (the wizard leaves the page), so a second click that lands once the
  // first request has already answered cannot send the same invitations again; only a failure releases it.
  const inFlightRef = useRef(false);

  const resetError = useCallback(() => setError(null), []);

  const submit = useCallback(
    async (state, { roleNames = [] } = {}) => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      setSubmitting(true);
      setError(null);
      try {
        const { body } = validateApiResponse(await createMembers(buildPayload(state)));
        const result = { createdCount: body.created_count, sent: state.sendEmail };
        const count = result.createdCount;
        const parts = [
          state.sendEmail
            ? t('invite.toast.sentMessage', { count })
            : t('invite.toast.createdMessage'),
          roleNames.length > 0 && t('invite.toast.roles', { roles: roleNames.join(', ') }),
        ];
        toast.success({
          title: state.sendEmail
            ? t('invite.toast.sentTitle')
            : t('invite.toast.createdTitle', { count }),
          message: parts.filter(Boolean).join(' '),
        });
        onSuccess?.(result);
      } catch (err) {
        inFlightRef.current = false;
        setError(err);
      } finally {
        setSubmitting(false);
      }
    },
    [onSuccess, t, toast],
  );

  return useMemo(
    () => ({ submit, submitting, error, resetError }),
    [submit, submitting, error, resetError],
  );
};
